import { supabase } from '@cleanpro/common-ui';
import { Business } from './business';

type BusinessRow = {
  id: string;
  business_name: string;
  owner_name: string;
  email: string;
  phone: string;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  business_type: string;
  status: string;
  contract_start_date: string | null;
  services_offered: string[] | null;
  hourly_rate: number | null;
  monday: boolean | null;
  tuesday: boolean | null;
  wednesday: boolean | null;
  thursday: boolean | null;
  friday: boolean | null;
  saturday: boolean | null;
  sunday: boolean | null;
  rating: number | null;
  completed_jobs: number | null;
  insurance_provider: string | null;
  insurance_policy_number: string | null;
  insurance_expiration_date: string | null;
  user_id: string | null;
  wallet_balance: number | null;
  growth_ads_enabled: boolean | null;
  growth_ads_paused: boolean | null;
  max_leads_per_week: number | null;
  created_at: string;
  updated_at: string;
};

const transformBusiness = (row: BusinessRow): Business => ({
  id: row.id,
  businessName: row.business_name,
  ownerName: row.owner_name,
  email: row.email,
  phone: row.phone,
  address: {
    addressLine1: row.address_line_1 || '',
    addressLine2: row.address_line_2 || '',
    addressCity: row.city || '',
    addressState: row.state || '',
    addressPostalCode: row.postal_code || '',
  },
  businessType: row.business_type as 'independent_contractor',
  status: (row.status as 'active' | 'inactive' | 'pending'),
  contractStartDate: row.contract_start_date || '',
  servicesOffered: row.services_offered || [],
  hourlyRate: row.hourly_rate ?? 0,
  availability: {
    monday: row.monday ?? false,
    tuesday: row.tuesday ?? false,
    wednesday: row.wednesday ?? false,
    thursday: row.thursday ?? false,
    friday: row.friday ?? false,
    saturday: row.saturday ?? false,
    sunday: row.sunday ?? false,
  },
  rating: row.rating ?? 0,
  completedJobs: row.completed_jobs ?? 0,
  insuranceInfo: row.insurance_provider ? {
    provider: row.insurance_provider,
    policyNumber: row.insurance_policy_number ?? '',
    expirationDate: row.insurance_expiration_date ?? '',
  } : undefined,
  userId: row.user_id ?? undefined,
  walletBalance: Number(row.wallet_balance) || 0,
  growthAdsEnabled: row.growth_ads_enabled ?? false,
  growthAdsPaused: row.growth_ads_paused ?? false,
  maxLeadsPerWeek: row.max_leads_per_week ?? 20,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const fetchAllBusinesses = async (): Promise<Business[]> => {
  const { data, error } = await supabase
    .from('businesses')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching businesses:', error);
    throw error;
  }

  return data ? data.map(transformBusiness) : [];
};
